import { ILogService } from "../../log/common/log";
import { IPerformanceService } from "../../../editor/services/performance/common/performance";
import { AIToolCallResponse, FunctionAiTool, IAITool } from "../common/ai";
import { getResourceEntries } from "../../../base/browser/resourceTimings";
import { buildPerformanceReport, gpuTimingForTool } from "./renderStatsReport";
import { IGpuTimingService } from "../../webgl/common/gpuTiming";
import { ILicenseService } from "../../auth/common/licenses";
import { IProductService } from "../../product/common/productService.js";
import { commerceAllowed } from "../../product/common/commercePolicy.js";

type MeasurePerformanceArguments = {
    durationMs: number;
    includeResourceTimings?: boolean;
    measureGpu?: boolean;
}

export class MeasurePerformanceTool extends FunctionAiTool {

    constructor(
        @ILogService private readonly logger: ILogService,
        @IPerformanceService private readonly performance: IPerformanceService,
        @IGpuTimingService private readonly gpuTiming: IGpuTimingService,
        @ILicenseService private readonly licenses: ILicenseService,
        @IProductService private readonly product: IProductService,
    ) {
        super();
    }

    readonly name = "measure_website_performance";
    readonly description = "Measures the performance of the current web application over a specified duration and provides a report on various performance metrics including timing and memory usage, browser information, resource load times, and — when a three.js / Needle Engine renderer is present — live render stats (draw calls, triangles, passes), scene contents, device-aware good/bad ratings, and a 0-100 performance score with the categories that made it.";
    readonly parametersJSONSchema = {
        type: "object" as const,
        properties: {
            durationMs: {
                type: "number" as const,
                description: "The duration in milliseconds to run the performance measurement. Minimum is 300ms and maximum is 5000ms.",
                default: 2000,
            },
            includeResourceTimings: {
                type: "boolean" as const,
                description: "Whether to include resource timing information in the report.",
                default: true,
            },
            measureGpu: {
                type: "boolean" as const,
                description: "Measure how long the GPU actually works per frame, and per render pass, with timer queries. "
                    + "Costs about half a second of profiling (it wraps the page's draw calls) and requires Needle Inspector Pro. "
                    + "Use it when the frame rate is below target and you need to know whether the GPU or the CPU is the bottleneck.",
                default: false,
            },
        },
        required: ["durationMs"],
    };

    canRun(): boolean {
        const canRun = typeof performance !== "undefined";
        return canRun;
    }

    call(parameters?: MeasurePerformanceArguments): Promise<AIToolCallResponse> {

        const durationMs = parameters?.durationMs || 2000;

        this.logger.info(`Starting performance measurement for ${durationMs} ms...`, parameters);


        return new Promise<AIToolCallResponse>((resolve) => {



            const previousLongAnimationFrames = performance.getEntriesByType("long-animation-frame");
            console.debug("Previous long-animation-frame entries:", previousLongAnimationFrames);
            const capturedEntries = new Array<PerformanceEntry>();
            const observer = new PerformanceObserver((list) => {
                const entries = list.getEntries();
                console.debug("Captured long-animation-frame entries:", entries);
                capturedEntries.push(...entries);
            });
            observer.observe({ type: "long-animation-frame", buffered: true });


            performance.mark("measure_performance_start");

            setTimeout(async () => {
                performance.mark("measure_performance_end");
                performance.measure("performance_measurement", "measure_performance_start", "measure_performance_end");

                let report = "";

                const browserInfo = await this.getBrowserInformation();
                report += browserInfo + "\n";

                const networkInfo = this.getNetworkInformation();
                report += networkInfo + "\n";

                const timingInfo = await this.getTimingInformation();
                report += timingInfo + "\n";

                // Get timing info
                const measures = performance.getEntriesByName("performance_measurement");
                if (measures.length > 0) {
                    const measure = measures[0];
                    report += `Performance Timing measured when the tool was called:\nDuration: ${measure.duration} ms\nStart Time: ${measure.startTime} ms\n`;
                } else {
                    report += "No performance timing information available.\n";
                }

                // Get memory info
                const memoryReport = await this.collectMemoryInfo();
                report += memoryReport;

                // Renderer + scene stats and device-aware ratings, from the same
                // IPerformanceService the Performance panel reads.
                report += "\n" + await this.collectRuntimeStats(parameters?.measureGpu === true);

                if (parameters?.includeResourceTimings !== false) {
                    const resourceTimings = await this.getResourceTimings();
                    report += resourceTimings + "\n";
                }

                // Process captured long animation frame entries
                const newCapturedEntries = capturedEntries.filter(entry => !previousLongAnimationFrames.includes(entry));
                const performanceEntriesReport = this.processCapturedPerformanceEntries(newCapturedEntries);
                report += performanceEntriesReport + "\n";

                this.logger.info("Performance measurement completed.");
                // this.logger.debug("Performance Report:\n", report);
                console.debug("Performance Report:\n", report);

                resolve({
                    result: {
                        success: true,
                        message: report,
                    }
                });

                // Clean up marks and measures
                performance.clearMarks("measure_performance_start");
                performance.clearMarks("measure_performance_end");
                performance.clearMeasures("performance_measurement");

            }, durationMs);
        });

    }


    /** The same document the panel's copy button produces, minus the footer (the
     *  AI does not need marketing links) — so the two can never drift apart. */
    private async collectRuntimeStats(measureGpu: boolean): Promise<string> {
        // The refusal/failure note goes INTO the report: an AI that receives no
        // GPU section and no reason concludes the GPU is fine.
        const gpu = measureGpu
            ? await gpuTimingForTool(this.gpuTiming, this.licenses.isInspectorAccessAllowed, commerceAllowed(this.product))
            : {};
        const report = await buildPerformanceReport(this.performance, {
            productName: "Needle Inspector",
            url: typeof location !== "undefined" ? location.href : undefined,
            at: Date.now(),
            gpuTiming: gpu.timing,
            includeFooter: false,
        });
        return gpu.note ? `${report}
_${gpu.note}_
` : report;
    }

    private async collectMemoryInfo(): Promise<string> {

        let info = "";

        // Get memory info
        if (performance.memory) {
            const memory = performance.memory;
            this.logger.info("Performance Memory Info:", memory);
            info += `Performance Memory Info:\nJS Heap Size Limit: ${memory.jsHeapSizeLimit}\nTotal JS Heap Size: ${memory.totalJSHeapSize}\nUsed JS Heap Size: ${memory.usedJSHeapSize}\n`;
        }

        // Get user agent specific memory info if available
        if (performance.measureUserAgentSpecificMemory) {
            const memoryInfo = await performance.measureUserAgentSpecificMemory();
            this.logger.info("Performance Memory Info:", memoryInfo);
            info += `Performance Memory Measurement:\nTotal Bytes: ${memoryInfo.bytes}\nBreakdown:\n`;
            for (const item of memoryInfo.breakdown) {
                info += ` - Types: ${item.types.join(", ")}, Bytes: ${item.bytes}\n`;
            }
        }

        return info;
    }

    private async getBrowserInformation(): Promise<string> {
        let info = "Browser Information:\n";
        info += `User Agent: ${navigator.userAgent}\n`;
        info += `Platform: ${navigator.platform}\n`;
        info += `Language: ${navigator.language}\n`;
        return info;
    }

    private getNetworkInformation() {
        if (!navigator.connection) {
            return "Network Information: Not available\n";
        }
        const connection = navigator.connection;
        let info = "Network Information:\n";
        info += ` - Downlink: ${connection.downlink} Mbps\n`;
        if (connection.downlinkMax) {
            info += ` - Downlink Max: ${connection.downlinkMax} Mbps\n`;
        }
        info += ` - Effective Type: ${connection.effectiveType}\n`;
        if (connection.rtt) {
            info += ` - RTT: ${connection.rtt} ms\n`;
        }
        if (connection.saveData !== undefined) {
            info += ` - Save Data: ${connection.saveData}\n`;
        }
        if (connection.type) {
            info += ` - Type: ${connection.type}\n`;
        }
        return info;
    }

    private async getTimingInformation(): Promise<string> {
        let info = "Timing Information:\n";
        if (performance.timing) {
            // PerformanceTiming has no index signature (and is deprecated), so the
            // walk needs an explicit record view; only the NUMBERS are timings —
            // the object also carries toJSON.
            const timing = performance.timing as unknown as Record<string, unknown>;
            for (const key in timing) {
                const value = timing[key];
                if (typeof value !== "number") continue;
                info += ` - ${key}: ${value}\n`;
            }
        } else {
            info += "No timing information available.\n";
        }
        return info;
    }

    private async getResourceTimings(): Promise<string> {
        let info = "Resource Timings:\n";
        const resources = getResourceEntries();
        for (const resource of resources) {
            // TODO: this can easily be a LOT of data
            if (resource.entryType === "resource") {
                const data = resource as PerformanceResourceTiming;
                info += ` - Name: ${data.name}, Start Time: ${data.startTime}, Duration: ${data.duration}`;
                if (data.initiatorType) {
                    info += `, Initiator Type: ${data.initiatorType}`;
                }
                if (data.transferSize) {
                    info += `, Transfer Size: ${data.transferSize} bytes`;
                }
                if (data.encodedBodySize) {
                    info += `, Encoded Body Size: ${data.encodedBodySize} bytes`;
                }
                if (data.decodedBodySize) {
                    info += `, Decoded Body Size: ${data.decodedBodySize} bytes`;
                }
                if (data.serverTiming && data.serverTiming.length > 0) {
                    info += `, Server Timing: [`;
                    for (const timing of data.serverTiming) {
                        info += `{ name: ${timing.name}, duration: ${timing.duration}, description: ${timing.description} }, `;
                    }
                    info += `]`;
                }
                info += "\n";
            }
        }
        return info;
    }

    private processCapturedPerformanceEntries(entries: PerformanceEntry[]): string {
        if (entries.length === 0) {
            return "No Long Animation Frame Entries captured during the measurement period.\n";
        }
        let info = "Captured Long Animation Frame Entries:\n";
        for (const entry of entries) {
            info += ` - Name: ${entry.name}, Start Time: ${entry.startTime}, Duration: ${entry.duration}\n`;
        }
        return info;
    }

}
